import React from 'react';
import { useGlobalActiveGeography, useGlobalSelectedId, useGlobalIsSmallScreen } from '../../data/StatusStore';
import { processSparklineTags } from './InlineSparkline';
import './WideBlurb.scss'; // Import the SCSS file

export interface WideBlurbProps {
  options: any;
  data: any;
  width: number;
  height: number;
  colorClass: string;
}

/**
 * Recursively parses custom markup.
 * 
 * Supported tags (case-insensitive) include:
 *   H1, red, teal, green, b, p
 * 
 * The syntax supports two formats:
 *   - Full form: [tag] ... [/tag]
 *   - Shorthand (toggle) form: [tag/] ... [tag/]
 * 
 * When a tag is encountered, we recursively parse until we see either a closing token in the form
 * [/tag] or a shorthand closing token in the form [tag/]. All text outside tags is passed through.
 */

const parseCustomMarkup = (input: string): string => {
  let pos = 0;

  // The recursive function parses text until it finds the given stopTag (if any).
  // If stopTag is undefined, it parses until the end.
  function parseUntil(stopTag?: string): string {
    let result = '';

    while (pos < input.length) {
      if (input[pos] === '[') {
        // Look ahead for the next closing bracket
        const end = input.indexOf(']', pos);
        if (end === -1) {
          // No closing bracket—append the rest of the text
          result += input.slice(pos);
          pos = input.length;
          break;
        }
        // Extract the token inside the brackets.
        let token = input.slice(pos + 1, end).trim();
        // Check if this token is a closing token for our current tag.
        // We consider both [/tag] and [tag/] as valid closers.
        if (stopTag) {
          const lowerToken = token.toLowerCase();
          if (lowerToken === '/' + stopTag.toLowerCase() || lowerToken === stopTag.toLowerCase() + '/') {
            pos = end + 1;
            return result;
          }
        }
        // Not a closing token—so this must be an opening token.
        pos = end + 1; // Move past the token
        // Determine if this is a shorthand (toggle) tag (ends with a slash)
        let isShorthand = false;
        if (token.endsWith('/')) {
          isShorthand = true;
          token = token.slice(0, -1).trim();
        }
        const tagName = token; // e.g., "H1", "red", "b", etc.
        // Recursively parse the content inside this tag.
        const inner = parseUntil(tagName);
        // Append the HTML tag with a custom CSS class.
        result += `<${tagName.toLowerCase()} class="custom-${tagName.toLowerCase()}">` + inner + `</${tagName.toLowerCase()}>`;
      } else {
        // Append literal character
        result += input[pos];
        pos++;
      }
    }
    return result;
  }

  // Wrap the entire output in a container span.
  return `<span class="custom-text-plain">` + parseUntil() + `</span>`;
};

const WideBlurb: React.FC<WideBlurbProps> = ({ options, data, width, height, colorClass }) => {
  // Retrieve the text content from the options.
  const contentTemplate = options.variables[0].sort["Stories Content"];
  const variableName = options.variables[0].variable;
  const activeGeography = useGlobalActiveGeography();
  const selectedId = useGlobalSelectedId();
  const isSmallScreen = useGlobalIsSmallScreen();
  // Optionally substitute dynamic content (for example, replacing "{}" with a value)
  let contentText = contentTemplate;
  if (selectedId) {
    let prefix = "";
    let suffix = selectedId;
    let geoDir = activeGeography === 'Tract' ? 'NTA' : activeGeography;
    let dynamicContent = "";
    if (activeGeography === 'NTA' || activeGeography === 'Tract') {
      prefix = selectedId ? selectedId.slice(0, 2).toLowerCase() : '';
      suffix = selectedId ? selectedId.slice(-4) : '';
    }
    if (activeGeography === 'CD') {
      suffix = `cd${selectedId}`;
    }
    if (activeGeography === 'NTA' || activeGeography === 'Tract') {
      dynamicContent = `${geoDir}/${prefix}/${prefix}${suffix}`;
    } else {
      dynamicContent = `${geoDir}/${suffix}`;
    }
    //console.log("G041425 dynamicContent", dynamicContent);
    //console.log("G041425 contentTemplate", contentTemplate);
    contentText = (dynamicContent && contentTemplate) ? contentTemplate.replace("{}", dynamicContent) : "";
  }

  // Parse custom markup and inject inline sparklines.
  const sortedArray = data?.dataArray?.[variableName];
  const htmlContent = processSparklineTags(
    contentText || '',
    parseCustomMarkup,
    sortedArray,
    colorClass,
    variableName
  );

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        lineHeight: '12pt',
        maxWidth: '600px',
        overflowY: 'auto',
        boxSizing: 'border-box',
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
    </div>
  );
};

export default WideBlurb;
